import moment from 'moment';
import { useState, useRef, useEffect } from 'react';
import IFormError from '../../../../interfaces/IFormError';
import IWizardStep from '../../../../interfaces/IWizardStep';
import WizardButton from '../../components/WizardButton';
import WizardTextField from '../../components/WizardTextField';
import * as yup from 'yup';
import { useValidateSchema } from '../../../../hooks/providers/useValidateSchema';
import { useFocus } from '../../../../hooks/util/useFocus';
import { IconButton, InputAdornment } from '@mui/material';
import { useToastrVanila } from '../../../../hooks/util/useToastrVanila';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import KeyboardArrowLeftOutlinedIcon from '@mui/icons-material/KeyboardArrowLeftOutlined';

import './PeriodDate.css';

interface PeriodDateProps extends IWizardStep { }

const PeriodDate = (props: PeriodDateProps) => {
  const { quantityTimeInDaysDefault } = props.medicalCertificate.typeDocument!;

  const [daysQuantity, setDaysQuantity] = useState(
    (props?.medicalCertificate?.timeQuantity && props?.medicalCertificate?.timeQuantity / 24) ||
    quantityTimeInDaysDefault,
  );
  const [errors, setErrors] = useState<IFormError | null>(null);
  const [loading, setLoading] = useState(false);
  const validationProvader = useValidateSchema();
  const buttonRef = useRef<HTMLInputElement>();
  const toastr = useToastrVanila();

  const focus = useFocus();

  const schema = yup.object({
    timeQuantity: yup
      .number()
      .typeError('Informe um número válido')
      .integer('O valor deve ser inteiro')
      .min(1, 'Valor deve ser maior do que zero')
      .max(999, 'O máximo permitido é 999')
      .required('Campo obrigatório'),
  });

  const handleSubmit = async () => {
    const result = await validationProvader.validate(schema, {
      timeQuantity: daysQuantity,
    });
    setErrors({ ...result.errors });
    if (result.errors) {
      toastr.error(result.errors.timeQuantity);
    }
    if (!result.isValid) {
      return;
    }

    const start = moment(props.medicalCertificate.documentDate)
      .startOf('day')
      .format();

    const end = moment(start)
      .add(daysQuantity, 'day')
      .subtract(1, 'minute')
      .format();

    setLoading(true);
    const newData = {
      typeTime: 'DAY',
      startTime: start,
      endTime: end,
      timeQuantity: parseInt(`${daysQuantity}`) * 24 || 0,
    };
    props.setStep(props.step + 1, { ...props.medicalCertificate, ...newData }, props.step);
  };

  const incrementDay = () => {
    setDaysQuantity(parseInt(`${daysQuantity}`) ? parseInt(`${daysQuantity}`) + 1 : 1);
  };

  const decrementDay = () => {
    setDaysQuantity(daysQuantity && parseInt(`${daysQuantity}`) > 0 ? parseInt(`${daysQuantity}`) - 1 : 0);
  };

  useEffect(() => {
    if (props.medicalCertificate.typeTime === 'HOUR') {
      if (quantityTimeInDaysDefault) {
        setDaysQuantity(quantityTimeInDaysDefault);
      } else {
        setDaysQuantity(0);
      }
    }
  }, []);

  return (
    <form>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px' }}>
        <IconButton
          onClick={decrementDay}
          sx={{
            mt: 2,
            border: '1px solid rgba(32, 144, 136, 0.5)',
            color: 'rgb(32, 144, 136)',
            '&:hover': { backgroundColor: 'rgba(32, 144, 136, 0.1)' },
          }}
        >
          <KeyboardArrowLeftOutlinedIcon fontSize="large" />
        </IconButton>

        <WizardTextField
          type="number"
          sx={{ marginTop: '16px' }}
          placeholder="Quantidade de dias"
          value={daysQuantity}
          onChange={e => setDaysQuantity(e.target.value ? parseInt(e.target.value) : 0)}
          error={Boolean(errors?.daysQuantity)}
          helperText={errors?.daysQuantity}
          onKeyUpCapture={e => {
            focus.setFocus(e, buttonRef);
          }}
          InputProps={{
            sx: { height: 48, fontSize: '1.2rem' },
            endAdornment: <InputAdornment position="end" />,
          }}
          inputProps={{
            style: { textAlign: 'center' },
          }}
        />

        <IconButton
          onClick={incrementDay}
          sx={{
            mt: 2,
            border: '1px solid rgba(32, 144, 136, 0.5)',
            color: 'rgb(32, 144, 136)',
            '&:hover': { backgroundColor: 'rgba(32, 144, 136, 0.1)' },
          }}
        >
          <KeyboardArrowRightOutlinedIcon fontSize="large" />
        </IconButton>
      </div>

      <WizardButton
        isLoading={loading}
        disabled={loading}
        sx={{ marginTop: '100px' }}
        onClick={handleSubmit}
        ref={buttonRef}
      >
        PRÓXIMO
      </WizardButton>
    </form>
  );
};

export default PeriodDate;
